import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { CheckCircle, Clock, AlertCircle } from "lucide-react";
import DocumentManager from "../components/documentManager";

export interface InvoiceData {
  id: string;
  milestoneName: string;
  amount: number;
  percentage: number;
  status: "pending" | "uploaded" | "paid";
  invoiceUrl?: string;
  uploadedAt?: string;
  invoiceNumber?: string;
}

interface InvoiceManagementProps {
  milestones: Array<{
    id: string;
    name: string;
    percentage: number;
  }>;
  totalAmount: number;
  clientName: string;
  projectName: string;
  projectId?: string;
  onInvoiceUpdate?: (invoices: InvoiceData[]) => void;
  initialInvoices?: InvoiceData[];
}

const InvoiceManagement: React.FC<InvoiceManagementProps> = ({
  milestones,
  totalAmount,
  clientName,
  projectName,
  projectId,
  onInvoiceUpdate,
  initialInvoices = [],
}) => {
  const [invoices, setInvoices] = useState<InvoiceData[]>(
    milestones.map((milestone, index) => {
      const existingInvoice = initialInvoices.find(
        (inv) => inv.id === milestone.id
      );
      return (
        existingInvoice || {
          id: milestone.id,
          milestoneName: milestone.name,
          amount: (totalAmount * milestone.percentage) / 100,
          percentage: milestone.percentage,
          status: "pending" as const,
        }
      );
    })
  );

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Format currency
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);

  // Clean filename for S3 path
  const cleanFileName = (name: string) => {
    return name
      .replace(/[^a-zA-Z0-9]/g, "_")
      .replace(/_+/g, "_")
      .toLowerCase();
  };

  // Handle successful upload
  const handleUploadSuccess = (milestoneId: string, result: any) => {
    setInvoices((prev) =>
      prev.map((invoice) =>
        invoice.id === milestoneId
          ? {
              ...invoice,
              status: "uploaded" as const,
              invoiceUrl: result.invoiceUrl,
              uploadedAt: result.uploadedAt,
              invoiceNumber: result.invoiceNumber,
            }
          : invoice
      )
    );

    // Clear any errors for this milestone
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[milestoneId];
      return newErrors;
    });
  };

  // Handle successful deletion
  const handleDeleteSuccess = (milestoneId: string) => {
    setInvoices((prev) =>
      prev.map((invoice) =>
        invoice.id === milestoneId
          ? {
              ...invoice,
              status: "pending" as const,
              invoiceUrl: undefined,
              uploadedAt: undefined,
              invoiceNumber: undefined,
            }
          : invoice
      )
    );
  };

  // Handle upload errors (this would be called by error handling in the parent or DocumentManager)
  const handleUploadError = (milestoneId: string, error: string) => {
    setErrors((prev) => ({
      ...prev,
      [milestoneId]: error,
    }));
  };

  // Effect to notify parent of changes
  useEffect(() => {
    if (onInvoiceUpdate) {
      onInvoiceUpdate(invoices);
    }
  }, [invoices, onInvoiceUpdate]);

  const getStatusIcon = (status: InvoiceData["status"]) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "uploaded":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "paid":
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: InvoiceData["status"]) => {
    switch (status) {
      case "pending":
        return "Pending Upload";
      case "uploaded":
        return "Invoice Uploaded";
      case "paid":
        return "Payment Received";
      default:
        return "Unknown";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Invoice Management</h3>
          <p className="text-sm text-muted-foreground">
            Upload invoices for each payment milestone
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-muted-foreground">
            Total Project Value
          </div>
          <div className="text-xl font-bold">{formatCurrency(totalAmount)}</div>
        </div>
      </div>

      <Tabs defaultValue={invoices[0]?.id} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {invoices.map((invoice, index) => (
            <TabsTrigger
              key={invoice.id}
              value={invoice.id}
              className="flex items-center gap-2"
            >
              {getStatusIcon(invoice.status)}
              <span className="hidden sm:inline">Milestone {index + 1}</span>
              <span className="sm:hidden">M{index + 1}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {invoices.map((invoice) => (
          <TabsContent key={invoice.id} value={invoice.id}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(invoice.status)}
                    {invoice.milestoneName}
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">
                      {formatCurrency(invoice.amount)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {invoice.percentage}% of total
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status Badge */}
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${
                      invoice.status === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : invoice.status === "uploaded"
                        ? "bg-green-100 text-green-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {getStatusText(invoice.status)}
                  </span>
                  {invoice.uploadedAt && (
                    <span className="text-xs text-muted-foreground">
                      Uploaded:{" "}
                      {new Date(invoice.uploadedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {/* Document Manager */}
                <DocumentManager
                  label={`Invoice for ${invoice.milestoneName}`}
                  field="invoice"
                  existingDocument={invoice.invoiceUrl}
                  documentId={invoice.invoiceNumber}
                  onFileChange={() => {}} // Not used in auto mode
                  onDeleteDocument={() => handleDeleteSuccess(invoice.id)}
                  onUploadSuccess={(field, result) =>
                    handleUploadSuccess(invoice.id, result)
                  }
                  accept=".pdf"
                  error={errors[invoice.id]}
                  uploadEndpoint={`${process.env.REACT_APP_BACKEND_URL}api/projects/upload-invoice`}
                  deleteEndpoint={`${process.env.REACT_APP_BACKEND_URL}api/projects/invoice/${invoice.id}`}
                  customUploadData={{
                    milestoneId: invoice.id,
                    clientName: cleanFileName(clientName),
                    projectName: cleanFileName(projectName),
                    projectId: projectId || "",
                  }}
                  uploadMode="auto"
                  documentType="invoice"
                  maxSizeMessage="Maximum file size: 10MB"
                  supportedFormatsMessage="Only PDF files are supported"
                />
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default InvoiceManagement;
